import { useRef, useState, useEffect } from 'react'
import trueIcon from '@assets/documents/true.webp'
import falseIcon from '@assets/documents/false.webp'
import naIcon from '@assets/documents/na.webp'

const useInputHandlers = (onChange) => ({
    onClick: (e) => e.currentTarget.focus(),
    onChange: (e) => onChange(e.target.value),
    onKeyDown: (e) => {
        if (e.key === 'Enter' && e.currentTarget.tagName === 'INPUT') {
            e.preventDefault()
            e.currentTarget.blur()
        }
    }
})

function TextField({
    value,
    readonly,
    onChange,
    fontSize,
    isTextArea,
    align='right'
}) {
    const handlers = useInputHandlers(onChange)
    const commonStyles = {
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0,
        pointerEvents: 'auto',
        border: 0,
        textAlign: align,
        fontSize: `${(fontSize/1.65).toFixed(2)}px`,
        color: '#3a70bd',
        backgroundColor: 'transparent',
        direction: 'ltr'
    }

    return isTextArea ? (
        <textarea
            {...handlers}
            dir='rtl'
            value={value || ''}
            style={{
                ...commonStyles,
                resize: 'none',
                lineHeight: 1.3,
                fontFamily: 'inherit'
            }}
            readOnly={readonly}
        />
    ) : (
        <input
            {...handlers}
            type='text'
            dir='rtl'
            value={value || ''}
            style={commonStyles}
            readOnly={readonly}
        />
    )
}

const CHOICE_ICON = { yes: trueIcon, no: falseIcon, na: naIcon }

function ChoiceField({
    value,
    onChange,
    options
}) {
    const handleClick = () => {
        const idx = options.indexOf(value)
        if (idx === -1) {
            onChange(options[0])
        } else if (idx === options.length - 1) {
            onChange(null)
        } else {
            onChange(options[idx + 1])
        }
    }

    return (
        <div
            onClick={handleClick}
            style={{
                width: '100%', height: '100%', pointerEvents: 'auto',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', userSelect: 'none', borderRadius: 4,
            }}
        >
            {value && (
                <img
                    src={CHOICE_ICON[value]}
                    alt={value}
                    draggable={false}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                />
            )}
        </div>
    )
}

function ImageField({
    value,
    onChange,
    readonly
}) {
    const inputRef = useRef(null)
    const [preview, setPreview] = useState(null)

    useEffect(() => {
        if (typeof value === 'string') {
            setPreview(value)
        } else if (value instanceof File) {
            const objectUrl = URL.createObjectURL(value)
            setPreview(objectUrl)
            return () => URL.revokeObjectURL(objectUrl)
        } else {
            setPreview(null)
        }
    }, [value])

    const handleFile = (e) => {
        const file = e.target.files?.[0]
        if (file) onChange(file)
    }

    return (
        <div
            onClick={() => !readonly && inputRef.current?.click()}
            style={{
                width: '100%', height: '100%', overflow: 'hidden', borderRadius: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'auto', cursor: readonly ? 'default' : 'pointer',
                border: (preview || readonly) ? '1px solid transparent' : '1px dashed #999',
                backgroundColor: (preview || readonly) ? 'transparent' : 'rgba(0,0,0,0.03)',
            }}
        >
            {!readonly && (
                <input
                    ref={inputRef}
                    type='file'
                    accept='image/*'
                    capture='environment'
                    onChange={handleFile}
                    style={{ display: 'none' }}
                />
            )}
            {preview 
                ? (
                    <img
                        src={preview}
                        alt=''
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                        }}
                    />
                )
                : (!readonly && <span style={{ fontSize: 11, color: '#777' }}>+ إضافة صورة</span>)
            }
        </div>
    )
}

export default function Field({
    type,
    value,
    onChange,
    fontSize,
    readonly,
    align
}) {
    switch (type) {
        case 'ta': return <TextField value={value} readonly={readonly} onChange={onChange} fontSize={fontSize} isTextArea align={align} />
        case 'y': return <ChoiceField value={value} onChange={onChange} options={['yes', 'na']} />
        case 'yn': return <ChoiceField value={value} onChange={onChange} options={['yes', 'no', 'na']} />
        case 'c': return <ChoiceField value={value} onChange={onChange} options={['yes']} />
        case 'i': return <ImageField value={value} onChange={onChange} readonly={readonly} />
        case 't': default: return <TextField value={value} readonly={readonly} onChange={onChange} fontSize={fontSize} align={align} />
    }
}